<template>
    <div class="mt-3 mb-3">
        <div class="h6 mb-0">
            <span
                class="collapse-icon text-dark mr-1"
                :class="panel_media_visible ? null : 'collapsed'"
                :aria-expanded="panel_media_visible ? 'true' : 'false'"
                @click="toggleMediaVisibility"
            >
                <span class="open">
                    <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                </span>
                <span class="closed">
                    <font-awesome-icon :icon="['fas', 'caret-right']" fixed-width />
                </span>
                <strong>Media</strong>
            </span>
        </div>
        <b-collapse
            v-model="panel_media_visible"
            :class="panel_media_visible ? null : 'collapsed'"
        >
            <div v-if="panel_media_visible">
                <div v-if="panel.media && panel.media.length" class="my-2 border-left pl-3">
                    <draggable
                        v-model="sorted_panel_media"
                        handle='.drag-handle'
                        @end="handleDragEnd"
                        :disabled="disabled"
                    >
                        <div v-for="(media, index) in sorted_panel_media" :key="index">
                            <b-card>
                                <div class="d-flex flex-row justify-content-between">
                                    <div class="drag-handle d-flex justify-content-center align-items-center mr-3">
                                        <font-awesome-icon
                                            :icon="['fal', 'bars']"
                                            size="2x"
                                            fixed-width
                                            style="color: gainsboro; cursor:grab"
                                        />
                                    </div>
                                    <panel-media-item
                                        :publication="publication"
                                        :article="article"
                                        :panel="panel"
                                        :item="media"
                                        :unit="unit"
                                        :type="'panel_media'"
                                        :disabled="disabled"
                                        @updateTitle="handleUpdateTitle(index, $event)"
                                        @updateLink="handleUpdateMediaItem(index, $event)"
                                        @deleteMediaItem="handleDeleteMediaItem(index)"
                                        @updateMediaItem="handleUpdateMediaItem(index, $event)"
                                    />
                                </div>
                            </b-card>
                        </div>
                    </draggable>
                </div>
                <b-alert
                    variant="danger"
                    :show="!panel.media || panel.media && !panel.media.length"
                    class="my-2"
                >
                    No Panel Media added
                </b-alert>

                <b-dropdown
                    size="sm"
                    class='ml-3'
                    variant="outline-primary"
                    dropup
                    right
                    :disabled="!can_update_panel || disabled"
                >
                    <template #button-content>
                        <font-awesome-icon :icon="['fad', 'plus-square']" /> Add Media
                    </template>
                    <b-dropdown-item @click.prevent="addExistingRelatedMedia(panel)">
                        <font-awesome-icon :icon="['fad', 'plus-square']" class="text-primary" fixed-width /> Import SBP Media
                    </b-dropdown-item>
                    <b-dropdown-item @click.prevent="addExistingPrintables(panel)">
                        <font-awesome-icon :icon="['fad', 'plus-square']" class="text-primary" fixed-width /> Import SBP Printables
                    </b-dropdown-item>
                </b-dropdown>

                <b-btn
                    size="sm"
                    class="ml-3"
                    variant="outline-primary"
                    @click="addPanelLink"
                    :disabled="!can_update_panel || disabled"
                >
                    <span>
                        <font-awesome-icon :icon="['fad', 'plus-square']"/> Add Link
                    </span>
                </b-btn>
            </div>
        </b-collapse>

        <add-related-media-modal
            v-model="show_add_related_media_modal"
            :bulk="true"
            @link="linkRelatedMediaItem"
            @link-multiple="linkRelatedMediaItems"
            @hide="show_add_related_media_modal = false"
        />
        <add-printable-modal
            v-model="show_add_printables_modal"
            @link="linkPrintableItem"
            @hide="show_add_printables_modal = false"
        />

        <link-modal
            :visible="show_link_modal"
            @hide="show_link_modal = false"
            @link="addPanelLinkItem"
        />
    </div>
</template>

<style lang="scss" scoped>
.panel-media-item + .panel-media-item {
    margin-top: 0.5rem;
}
</style>

<script>
import { mapState } from 'vuex'
import PanelMediaItem from '@/components/panels/panel_media/Item'
import AddRelatedMediaModal from '@/components/modals/article/related_media/AddRelatedMediaModal'
import AddPrintableModal from '@/components/modals/article/worksheets/AddPrintableModal'
import LinkModal from '@/components/modals/links/LinkModal.vue'
import draggable from 'vuedraggable'

export default {
    data: () => ({
        type: null,
        panel_media_visible: true,
        show_add_printables_modal: false,
        show_add_related_media_modal: false,
        show_link_modal: false,
        sorted_panel_media: [],
        new_panel_pdf_file: null,
        current_panel: null,
    }),
    props: [
        'publication',
        'unit',
        'article',
        'panel',
        'disabled',
    ],
    components: {
        LinkModal,
        PanelMediaItem,
        AddRelatedMediaModal,
        AddPrintableModal,
        draggable
    },
    computed: {
        ...mapState({
            user: state => state.user.user
        }),
        can_update_panel(){
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_panel');
        }
    },
    watch: {
    },
    methods: {
        toggleMediaVisibility() {
            this.panel_media_visible = !this.panel_media_visible
        },
        addExistingRelatedMedia(){
            this.show_add_related_media_modal = true;
        },
        addExistingPrintables(){
            this.show_add_printables_modal = true;
        },
        addPanelLink() {
            this.show_link_modal = true
        },
        uploadPdfAsPanelMedia (event, panel) {
            let file = null
            let pdf_language = null
            let form_data = new FormData()
            this.current_panel = panel;
            if (this.publication.language == 'es') {
                pdf_language = 'Spanish'
            } else {
                pdf_language = 'English'
            }
            if (
                event.target &&
                event.target.files
            ) {
                file = event.target.files[0]
                form_data.append('file', file);
                form_data.append('language', pdf_language);
                form_data.append('title', file.name);
                form_data.append('description', '');
                form_data.append('source_info', '');
                form_data.append('panel_id', panel.id);
            }

            if(file != null){
                this.axios.post('/api/panels/add_panel_pdf', form_data)
                    .then(response => {
                        if (
                            response.data &&
                            response.data.success
                        ) {
                            panel.media.push({
                                name: response.data.media.title,
                                language: response.data.media.language,
                                filename: response.data.media.title,
                                value: response.data.media.url,
                                splash_url: response.data.media.url
                            });
                            this.$emit('savePanel', this.current_panel)
                        }

                    })
                    .catch(error => {
                        if (error) {

                        }
                    })
            }
        },
        linkRelatedMediaItem(asset){
            let splash_url = asset.url;

            let media_language = asset.language ? asset.language : this.publication.language

            if(asset.splash_url){
                splash_url = asset.splash_url;
            }
            this.current_panel.media.push({
                related_media_id: asset.id,
                name: asset.title,
                value: asset.url,
                src: asset.url,
                alt: asset.alt,
                title: asset.title,
                description: asset.description,
                credit: asset.credit,
                caption: asset.caption,
                language: media_language,
                splash_url: asset.splash_url,
                type: asset.type,
            })
            this.$emit('savePanel', this.current_panel)
            this.show_add_related_media_modal = false;
            this.current_panel = null;
        },
        linkRelatedMediaItems(selected_items) {
            _.forEach(selected_items, selected_item => {
                const asset = selected_item.item
                let media_language = asset.language ? asset.language : this.publication.language

                this.current_panel.media.push({
                    related_media_id: asset.id,
                    name: asset.title,
                    value: asset.url,
                    src: asset.url,
                    alt: asset.alt,
                    title: asset.title,
                    description: asset.description,
                    credit: asset.credit,
                    caption: asset.caption,
                    language: media_language,
                    splash_url: asset.splash_url,
                    type: asset.type,
                })
            })

            this.$emit('savePanel', this.current_panel)
            this.show_add_related_media_modal = false;
            this.current_panel = null;
        },
        linkPrintableItem(asset){
            let splash_url = null;
            let panel_language = asset.language ? asset.language : this.publication.language

            this.current_panel.media.push({
                name: asset.title,
                value: asset.cdn_url,
                type: asset.type,
                language: panel_language,
                printable_id: asset.id,
            })
            this.$emit('savePanel', this.current_panel)
            this.show_add_printables_modal = false;
            this.current_panel = null;
        },
        addPanelLinkItem(link) {
            this.current_panel.media.push({
                type: 'link',
                name: link.name,
                value: link.url,
                // thumbnail properties
                src: link.thumbnail.src,
                alt: link.thumbnail.alt,
                title: link.thumbnail.title,
                description: link.thumbnail.description,
                caption: link.thumbnail.caption,
                credit: link.thumbnail.credit,
            })
            this.$emit('savePanel', this.current_panel)
            this.current_panel = null
        },
        handleDragEnd() {
            const updatedMedia = this.sorted_panel_media.map((media, index) => {
                return { ...media, panel_media_order_number: index + 1 };
            });

            // Emit an event to notify the parent about the updated media order
            this.$emit('updateMediaOrder', updatedMedia);
        },
        handleUpdateTitle(media_index, updatedMediaItem) {
            this.current_panel.media[media_index] = updatedMediaItem
            this.$emit('savePanel', this.current_panel)
            this.current_panel = null
        },
        handleDeleteMediaItem(media_index) {
            this.current_panel.media.splice(media_index, 1)
            this.$emit('savePanel', this.current_panel)
            this.current_panel = null
        },
        handleUpdateMediaItem(media_index, updatedMediaItem) {
            this.current_panel.media[media_index] = updatedMediaItem
            this.$emit('savePanel', this.current_panel)
            this.current_panel = null
        },
    },
    mounted () {
        this.sorted_panel_media = this.panel.media.slice().sort((a, b) => a.panel_media_order_number - b.panel_media_order_number);
        this.current_panel = this.panel
    }
}
</script>
